import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Badge, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTerminal, faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import c2Service from '../../services/c2Service';
import { useAuth } from '../../contexts/AuthContext';

const C2Terminal = ({ beacons, selectedBeacon, onSelectBeacon, wsRef }) => {
  const [commandHistory, setCommandHistory] = useState([]);
  const [currentCommand, setCurrentCommand] = useState('');
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [commandLog, setCommandLog] = useState([]);
  const terminalRef = useRef(null);
  const inputRef = useRef(null);
  const { user } = useAuth();

  // Load terminal history from localStorage when beacon changes
  useEffect(() => {
    if (selectedBeacon) {
      const storageKey = `terminal_log_${selectedBeacon.beaconId}`;
      const savedLog = localStorage.getItem(storageKey);
      if (savedLog) {
        try {
          const parsed = JSON.parse(savedLog);
          // Restore timestamps as Date objects
          const restored = parsed.map(entry => ({
            ...entry,
            timestamp: new Date(entry.timestamp)
          }));
          setCommandLog(restored);
        } catch (e) {
          console.error('Failed to parse terminal log:', e);
          setCommandLog([]);
        }
      } else {
        setCommandLog([]);
      }
    }
  }, [selectedBeacon]);

  // Save terminal history to localStorage whenever it changes
  useEffect(() => {
    if (selectedBeacon && commandLog.length > 0) {
      const storageKey = `terminal_log_${selectedBeacon.beaconId}`;
      localStorage.setItem(storageKey, JSON.stringify(commandLog));
    }
  }, [commandLog, selectedBeacon]);

  useEffect(() => {
    // Auto-scroll to bottom when new output appears
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [commandLog]);

  useEffect(() => {
    // Listen for WebSocket messages
    if (wsRef?.current) {
      // Store the original onmessage handler
      const originalOnMessage = wsRef.current.onmessage;

      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('[C2Terminal] WebSocket message:', message);

          // Show task completion results for the selected beacon
          if (message.type === 'task_completed') {
            console.log('[C2Terminal] Task completed - beaconId:', message.beaconId, 'selected:', selectedBeacon?.beaconId);

            // Show results if this is for the selected beacon, or if no beacon selected
            if (!selectedBeacon || selectedBeacon?.beaconId === message.beaconId) {
              const task = message.task || {};
              addToLog('system', `✓ Task completed: ${task.command} [${task._id || 'unknown'}]`, 'success');

              if (task.result?.output) {
                addToLog('output', task.result.output);
              }
              if (task.result?.error) {
                addToLog('error', `Error: ${task.result.error}`, 'danger');
              }
              if (task.result?.duration) {
                addToLog('info', `Duration: ${task.result.duration}ms`, 'info');
              }
            }
          }

          // Show task creation
          if (message.type === 'task_created') {
            if (!selectedBeacon || selectedBeacon?.beaconId === message.beaconId) {
              addToLog('info', `→ Task created: ${message.task?.command || 'unknown'}`, 'info');
            }
          }
        } catch (err) {
          console.error('[C2Terminal] Failed to parse WebSocket message:', err);
        }

        // Call original handler
        if (originalOnMessage) {
          originalOnMessage(event);
        }
      };
    }
  }, [wsRef, selectedBeacon]);

  const addToLog = (type, content, variant = 'info') => {
    setCommandLog(prev => [...prev, {
      id: Date.now(),
      type,
      content,
      variant,
      timestamp: new Date()
    }]);
  };

  const handleCommandSubmit = async (e) => {
    e.preventDefault();

    if (!currentCommand.trim()) return;
    if (!selectedBeacon) {
      addToLog('error', 'No beacon selected', 'danger');
      return;
    }

    // Parse command (support both "command" and "/command" syntax)
    let cmdInput = currentCommand.trim();
    // Strip leading slash if present
    if (cmdInput.startsWith('/')) {
      cmdInput = cmdInput.substring(1);
    }

    const parts = cmdInput.split(' ');
    const command = parts[0];
    const args = parts.slice(1).join(' ');

    // Add to history
    setCommandHistory(prev => [currentCommand, ...prev]);
    setHistoryIndex(-1);

    // Add to log
    addToLog('command', `${selectedBeacon.beaconId}> ${currentCommand}`, 'primary');

    // Clear input
    setCurrentCommand('');

    // Process special commands
    switch (command) {
      case 'help':
        showHelp();
        break;

      case 'clear':
        setCommandLog([]);
        // Clear localStorage for this beacon
        if (selectedBeacon) {
          const storageKey = `terminal_log_${selectedBeacon.beaconId}`;
          localStorage.removeItem(storageKey);
        }
        break;

      case 'select':
        if (args) {
          const beacon = beacons.find(b => b.beaconId.includes(args));
          if (beacon) {
            onSelectBeacon(beacon);
            addToLog('system', `Selected beacon: ${beacon.beaconId}`, 'success');
          } else {
            addToLog('error', `Beacon not found: ${args}`, 'danger');
          }
        } else {
          addToLog('error', 'Usage: select <beacon-id>', 'warning');
        }
        break;

      case 'beacons':
        listBeacons();
        break;

      case 'info':
        showBeaconInfo();
        break;

      default:
        // Send command to beacon
        await executeCommand(command, args);
    }
  };

  const executeCommand = async (command, argsString) => {
    try {
      addToLog('system', `Sending command to ${selectedBeacon.beaconId}...`, 'info');

      // Parse arguments based on command
      let args = {};
      switch (command) {
        case 'shell':
        case 'cmd':
        case 'exec':
          args = { cmd: argsString };
          command = 'shell';
          break;

        case 'sleep':
          args = { seconds: parseInt(argsString) || 60 };
          break;

        case 'screenshot':
        case 'ss':
          command = 'screenshot';
          args = {};
          break;

        case 'upload':
          const uploadParts = argsString.split(' ');
          args = { localPath: uploadParts[0], remotePath: uploadParts[1] };
          break;

        case 'download':
        case 'dl':
          command = 'download';
          args = { remotePath: argsString };
          break;

        case 'mimikatz':
        case 'mimi':
          command = 'mimikatz';
          args = { module: argsString || 'sekurlsa::logonpasswords' };
          break;

        case 'ps':
        case 'processes':
          command = 'process_list';
          args = {};
          break;

        case 'scan':
          command = 'network_scan';
          args = { target: argsString || '192.168.1.0/24' };
          break;

        default:
          // Try to execute as shell command
          args = { cmd: `${command} ${argsString}` };
          command = 'shell';
      }

      // Create task
      const response = await c2Service.createTask(selectedBeacon._id, command, args);
      const task = response.data.data || response.data;

      if (task.requiresApproval) {
        addToLog('warning', `Task requires approval (high-risk command)`, 'warning');
        addToLog('info', `Task ID: ${task._id}`, 'info');
      } else {
        addToLog('success', `Task queued (waiting for beacon check-in)`, 'success');
        addToLog('info', `Task ID: ${task._id}`, 'info');
      }
    } catch (error) {
      console.error('Command execution error:', error);
      addToLog('error', `Failed: ${error.response?.data?.error || error.message}`, 'danger');
    }
  };

  const showHelp = () => {
    const helpText = `
PurpleHQ C2 Terminal - Available Commands:

TERMINAL CONTROLS:
  help              Show this help message
  clear             Clear terminal output
  select <id>       Select beacon by ID
  beacons           List all active beacons
  info              Show selected beacon info

BEACON COMMANDS:
  shell <cmd>       Execute shell command
  cmd <cmd>         Alias for shell
  exec <cmd>        Alias for shell
  sleep <sec>       Change sleep interval
  screenshot        Capture screenshot
  upload <l> <r>    Upload file (local remote)
  download <path>   Download file
  mimikatz [mod]    Run Mimikatz module
  ps                List processes
  scan [target]     Network scan (CIDR)

KEYBOARD SHORTCUTS:
  ↑/↓               Navigate command history
  Tab               (Future: autocomplete)
  Ctrl+C            Clear current input
  Ctrl+L            Clear terminal

EXAMPLES:
  shell whoami
  sleep 120
  screenshot
  upload C:\\temp\\file.exe C:\\Windows\\temp\\file.exe
  download C:\\Users\\admin\\desktop\\passwords.txt
  mimikatz sekurlsa::logonpasswords
  scan 192.168.1.0/24
    `.trim();

    addToLog('help', helpText, 'info');
  };

  const listBeacons = () => {
    addToLog('system', `\n=== Active Beacons (${beacons.length}) ===`, 'info');
    beacons.forEach(beacon => {
      const status = beacon.isAlive ? 'ALIVE' : 'OFFLINE';
      const approved = beacon.isApproved ? 'APPROVED' : 'PENDING';
      const info = `[${status}][${approved}] ${beacon.beaconId} - ${beacon.systemInfo?.hostname} (${beacon.systemInfo?.os})`;
      addToLog('info', info, beacon.isAlive ? 'success' : 'secondary');
    });
  };

  const showBeaconInfo = () => {
    if (!selectedBeacon) {
      addToLog('error', 'No beacon selected', 'warning');
      return;
    }

    const info = `
=== Beacon Information ===
ID:           ${selectedBeacon.beaconId}
Hostname:     ${selectedBeacon.systemInfo?.hostname || 'N/A'}
OS:           ${selectedBeacon.systemInfo?.os || 'N/A'}
Architecture: ${selectedBeacon.systemInfo?.architecture || 'N/A'}
Username:     ${selectedBeacon.systemInfo?.username || 'N/A'}
Domain:       ${selectedBeacon.systemInfo?.domain || 'N/A'}
Admin:        ${selectedBeacon.systemInfo?.isAdmin ? 'Yes' : 'No'}
IP Addresses: ${selectedBeacon.systemInfo?.ipAddresses?.join(', ') || 'N/A'}
External IP:  ${selectedBeacon.systemInfo?.externalIp || 'N/A'}
Sleep:        ${selectedBeacon.config?.sleep || 60}s ± ${selectedBeacon.config?.jitter || 30}%
Last Seen:    ${new Date(selectedBeacon.lastSeen).toLocaleString()}
Check-ins:    ${selectedBeacon.checkInCount || 0}
Status:       ${selectedBeacon.isAlive ? 'Alive' : 'Offline'}
Approved:     ${selectedBeacon.isApproved ? 'Yes' : 'No'}
    `.trim();

    addToLog('info', info, 'info');
  };

  const handleKeyDown = (e) => {
    // Up arrow - previous command
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (historyIndex < commandHistory.length - 1) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setCurrentCommand(commandHistory[newIndex]);
      }
    }

    // Down arrow - next command
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setCurrentCommand(commandHistory[newIndex]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setCurrentCommand('');
      }
    }

    // Ctrl+C - clear input
    if (e.key === 'c' && e.ctrlKey) {
      e.preventDefault();
      setCurrentCommand('');
      setHistoryIndex(-1);
    }

    // Ctrl+L - clear terminal
    if (e.key === 'l' && e.ctrlKey) {
      e.preventDefault();
      setCommandLog([]);
    }
  };

  const getLogColor = (variant) => {
    const colors = {
      primary: '#0d6efd',
      success: '#198754',
      danger: '#dc3545',
      warning: '#ffc107',
      info: '#0dcaf0',
      secondary: '#6c757d'
    };
    return colors[variant] || colors.info;
  };

  // Admin has all permissions, or check specific permission
  const canInteract = user?.role === 'admin' || user?.permissions?.includes('c2.console.interact');

  return (
    <Card>
      <Card.Header className="bg-dark text-light">
        <Row className="align-items-center">
          <Col md={8}>
            <strong><FontAwesomeIcon icon={faTerminal} /> C2 Terminal</strong>
            {selectedBeacon && (
              <Badge bg={selectedBeacon.isAlive ? 'success' : 'secondary'} className="ms-2">
                {selectedBeacon.beaconId}
              </Badge>
            )}
          </Col>
          <Col md={4} className="text-end">
            <Form.Select
              size="sm"
              value={selectedBeacon?.beaconId || ''}
              onChange={(e) => {
                const beacon = beacons.find(b => b.beaconId === e.target.value);
                if (beacon) onSelectBeacon(beacon);
              }}
              style={{ width: 'auto', display: 'inline-block', backgroundColor: '#1f2937', color: '#e5e7eb', border: '1px solid #4b5563' }}
            >
              <option value="">Select beacon...</option>
              {beacons.filter(b => b.isApproved && b.isAlive).map(beacon => (
                <option key={beacon._id} value={beacon.beaconId}>
                  {beacon.beaconId} - {beacon.systemInfo?.hostname}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body className="p-0">
        {/* Terminal Output */}
        <div
          ref={terminalRef}
          className="bg-dark text-light p-3 font-monospace"
          style={{
            height: '500px',
            overflowY: 'auto',
            fontSize: '0.9em',
            lineHeight: '1.5'
          }}
          onClick={() => inputRef.current?.focus()}
        >
          {commandLog.length === 0 && (
            <div className="text-muted">
              <p>PurpleHQ C2 Terminal v1.0.0</p>
              <p>Type 'help' for available commands</p>
              <p className="mb-0">Select a beacon to begin...</p>
            </div>
          )}

          {commandLog.map(log => (
            <div
              key={log.id}
              style={{
                color: log.type === 'command' ? '#00ff00' :
                       log.type === 'error' ? '#ff6b6b' :
                       log.type === 'output' ? '#ffffff' :
                       log.type === 'help' ? '#ffd700' :
                       getLogColor(log.variant),
                marginBottom: '8px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              <small className="text-muted">
                [{log.timestamp.toLocaleTimeString()}]
              </small>{' '}
              {log.content}
            </div>
          ))}
        </div>

        {/* Terminal Input */}
        <Form onSubmit={handleCommandSubmit} className="bg-dark">
          <div className="d-flex align-items-center p-2 border-top border-secondary">
            <span className="font-monospace me-2" style={{ color: '#10b981', fontWeight: 'bold' }}>
              {selectedBeacon ? `${selectedBeacon.beaconId}>` : 'c2>'}
            </span>
            <Form.Control
              ref={inputRef}
              type="text"
              value={currentCommand}
              onChange={(e) => setCurrentCommand(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={!canInteract || !selectedBeacon}
              placeholder={
                !canInteract ? 'Read-only access' :
                !selectedBeacon ? 'Select a beacon first...' :
                'Enter command...'
              }
              className="font-monospace"
              style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #374151',
                color: '#e5e7eb',
                outline: 'none',
                boxShadow: 'none',
                flex: 1
              }}
              autoComplete="off"
              autoFocus
            />
            <Button
              type="submit"
              variant="success"
              size="sm"
              disabled={!canInteract || !selectedBeacon || !currentCommand.trim()}
              className="ms-2"
            >
              Execute
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default C2Terminal;
